import React from 'react';
import {action, computed, makeObservable, observable} from 'mobx';
import {observer} from 'mobx-react';
import './engraved-plate-extended.scss';
import {engravedPlateExtendedStore, EPLine, Font, FONT_FAMILY_MAP, FONT_OPTIONS, LineHeight, Size} from './engraved-plate-extended.store';
import s from '../../../s';
import {DefaultPCS, EPMethod} from '../../../rendering/render.def';
import {isNullOrWhiteSpace, Portal} from '../../../../util';
import ModalSimple from '../../../../global/modal-simple';
import InfoIcon from '../../shared/info-icon';
import {EPEditor} from '../ep-editor';

// NOTE FOR C# RENDERER

// Example custom_text_6 string (10 lines possible):
// 0:1-L-0-0:2-S-1-0:3-S-0-0
// Interpretation rules for renderer:
//  - Split by ':' to get per-line tokens
//  - First token is always fontIndex (0 or 1)
//  - All other line tokens => <lineNumber>-<lineHeight>-<bold>-<italic>
//  - LineHeight: S -> 0.25, M -> 0.375, L -> 0.5
//  - The total plate vertical height should be calculated as sum(lineHeights) + rail (0.25" default).

@observer
export default class EngravedPlateExtended extends React.Component {
    @observable lines: EPLine[] = [];
    @observable collapsed: boolean = true;
    @observable font: Font = Font.Roboto;

    constructor(props: any) {
        super(props);
        makeObservable(this);
        this.init();
    }

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || window.innerWidth < 960;
    }

    @action
    getDefaultLine = (value?: string) => {
        return {text: value || '', lineHeight: LineHeight.S, size: Size.S, bold: false, italic: false};
    };

    @action
    init = () => {
        // initialize lines from existing s.ps.ep1..ep10 if present so we preserve old behavior
        for (let i = 1; i <= 10; i++) {
            const val = s.ps[`ep${i}`] || '';

            if (val) {
                this.lines.push(this.getDefaultLine(val));
            }
        }

        // If no existing lines, default to 1 empty line
        if (this.lines.length === 0) {
            this.lines.push(this.getDefaultLine());
        }

        // push initial state into s.pcs so frame rendering can read it
        this.pushToPCS();
        this.getFontFamily();
        engravedPlateExtendedStore.lines = this.lines;
    };

    @action
    toggleCollapse = () => {
        this.collapsed = !this.collapsed;
    };

    // helper to compute custom_text_6 string required by backend/C#
    // first token is always fontIndex (0 or 1)
    // format per-line token: <lineNumber>-<LineHeight>-<Bold>-<Italic>
    // tokens joined by ':'
    buildCustomText6(): string {
        const lineTokens = this.lines.map((ln, idx) => {
            const lineNumber = idx + 1;
            const bold = ln.bold ? 1 : 0;
            const italic = ln.italic ? 1 : 0;

            return `${lineNumber}-${this.getSizeFromValue(ln.lineHeight)}-${bold}-${italic}`;
        });

        return `${this.font}:${lineTokens.join(':')}`;
    }

    // push all EP related pieces to s.pcs so renderer + pricing can react
    @action
    pushToPCS() {
        for (let i = 0; i < 10; i++) {
            const key = `ep${i + 1}`;

            s.ps[key] = this.lines[i] ? this.lines[i].text : '';
        }

        // set custom_text_6 to the encoded configuration string
        s.ps.custom_text_6 = this.buildCustomText6();

        // set dynamic EP height
        DefaultPCS.DefaultEPHeight = this.getEPHeight() + 0.25;

        // trigger UI/render/pricing updates
        s.ui.doRender = true;
        s.ui.update_pricing = true;
        s.pulse();
    }

    // called whenever a line's text changes
    @action
    onLineTextChange = (index: number, value: string) => {
        if (!this.lines[index]) return;

        this.lines[index].text = value;
        this.pushToPCS();
    };

    // add another line (max 10)
    @action
    addLine = () => {
        if (this.lines.length >= 10) return;

        this.lines.push(this.getDefaultLine());
        this.pushToPCS();
    };

    // remove a line at index
    @action
    removeLine = (index: number) => {
        if (index < 0 || index >= this.lines.length) return;

        this.lines.splice(index, 1);
        // ensure at least one line exists
        if (this.lines.length === 0) {
            this.lines.push();
        }

        this.pushToPCS();
    };

    // toggle size between S -> M -> L
    @action
    toggleSize = (index: number) => {
        const ln = this.lines[index];

        if (!ln) return;

        if (ln.lineHeight === LineHeight.S) {
            ln.lineHeight = LineHeight.M;
            ln.size = Size.M;
        } else if (ln.lineHeight === LineHeight.M) {
            ln.lineHeight = LineHeight.L;
            ln.size = Size.L;
        } else {
            ln.lineHeight = LineHeight.S;
            ln.size = Size.S;
        }

        this.pushToPCS();
    };

    @action
    toggleBold = (index: number) => {
        const ln = this.lines[index];

        if (!ln) return;

        ln.bold = !ln.bold;
        this.pushToPCS();
    };

    @action
    toggleItalic = (index: number) => {
        const ln = this.lines[index];

        if (!ln) return;

        ln.italic = !ln.italic;
        this.pushToPCS();
    };

    @action
    onFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.font = +e.target.value as Font;
        this.getFontFamily();
        this.pushToPCS();
    };

    @action
    ep_add = () => {
        // set a default ep_id if none
        if (!s.ps.ep_id || s.ps.ep_id <= 0) {
            s.ps.ep_id = s.ps.ep_id_view || 37;
        }

        s.ui.doRender = true;
        s.ps.lino_base_name = '';
        s.ps.lino_id = -1;
        s.ui.update_pricing = true;
        s.pulse();
    };

    @action
    ep_remove = () => {
        if (s.ps.ep_id > 0) {
            s.ps.ep_id = 0;
            s.ui.update_pricing = true;
            s.ui.doRender = true;
            s.pulse();
        }
    };

    getFontFamily = () => {
        engravedPlateExtendedStore.fontFamily = FONT_FAMILY_MAP[this.font] ?? FONT_FAMILY_MAP[Font.Roboto];
    };

    getSizeFromValue = (value: number) => {
        return (Object.keys(LineHeight) as Array<keyof typeof LineHeight>).find(key => LineHeight[key] === value);
    };

    getEPHeight = () => {
        return this.lines.reduce((acc, ln) => {
            if (!ln.text || isNullOrWhiteSpace(ln.text)) {
                return acc;
            }

            return acc + ln.lineHeight;
        }, 0);
    };

    render() {
        if (!s.ui.show_cust || !s.prod.EngravedPlateStatusID || s.prod.EngravedPlateStatusID < 1) {
            return null;
        }

        return (
            <div
                className="gray-box ep"
                style={{padding: 0}}
            >
                <ModalSimple
                    id="modal-sample-ep"
                    title="Engraved Plate Information"
                    maxWidth="600px"
                >
                    <div>
                        <ul>
                            {s.prod.EngravedPlateStatusID === 0 && <li>This product does not allow an engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 1 && <li>This product requires a gold engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 2 && <li>This product requires a silver engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 3 && <li>This product allows a gold engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 4 && <li>This product allows a silver engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 5 && <li>This product allows a gold or silver engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 6 && <li>This product requires a black engraved plate</li>}
                            {s.prod.EngravedPlateStatusID === 7 && <li>This product allows a black engraved plate</li>}
                            <li>Enter up to 30 characters per line</li>
                            <li>You may have up to 10 lines centered on the engraved plate</li>
                            <li>Dimensions: Depends on how many lines entered (plate image may be stretched vertically if needed)</li>
                            <li>If you do not provide text, a plate will not be included with your order</li>
                        </ul>
                        <div>
                            <b>Example engraved plate:</b>
                        </div>
                        <img
                            src={`https://${Portal?.CDNDomain}/images/products/full/80049pid25402.png`}
                            alt="Example engraved plate"
                            aria-label="Example engraved plate"
                        />
                    </div>
                </ModalSimple>

                <div className={this.canCollapse && s.ps.is_caf ? '' : ''}>
                    <div style={{paddingLeft: 12, paddingRight: 12, paddingTop: 0, paddingBottom: 0, position: 'relative'}}>
                        <div
                            role={this.canCollapse ? 'button' : undefined}
                            tabIndex={this.canCollapse ? 0 : undefined}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-none d-xl-block toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#ep-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'ep-inner' : undefined}
                        >
                            <div className="cust-desktop-head">
                                <span>Engraved Plate: </span>
                                <span>{s.ps.ep_id <= 0 ? '' : s.ps.ep_id === 37 ? 'Gold' : s.ps.ep_id === 1099 ? 'Black' : 'Silver'}</span>
                            </div>
                        </div>

                        <div
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-block d-xl-none toggle-bs"
                            data-bs-toggle={this.canCollapse ? 'collapse' : ''}
                            data-bs-target="#ep-inner"
                            aria-expanded={this.canCollapse ? false : undefined}
                            aria-controls={this.canCollapse ? 'ep-inner' : undefined}
                        >
                            <div className="cust-flex-head">
                                <div>
                                    <div
                                        className="header-std flexer cust-flex2"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        Engraved Plate
                                    </div>
                                    <div className="cust-flex2">{s.ps.ep_id <= 0 ? '' : s.ps.ep_id === 37 ? 'Gold' : s.ps.ep_id === 1099 ? 'Black' : 'Silver'}</div>
                                </div>
                                <div>
                                    {s.ps.ep_id > 0 && (
                                        <img
                                            className="th-h"
                                            src={s.ps.ep_id === 37 ? `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_gold_original_engravedplate.png` : `https://${Portal?.CDNDomain}/images/entities/engravedplate/ep_silver_original_engravedplate.png`}
                                            alt="Engraved Plate Thumbnail"
                                            style={{height: 48, width: 'auto'}}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="ep-inner"
                        className={this.canCollapse ? 'collapse' : ''}
                        style={{padding: '0 12px'}}
                    >
                        <div>
                            <div
                                className={`ep-container ${s.ps.ep_id <= 0 ? 'silver' : s.ps.ep_id === 37 ? 'gold' : 'silver'}`}
                                style={{textAlign: 'left', width: '100%'}}
                            >
                                {s.pcs.EPMethodID === EPMethod.Custom || s.pcs.EPMethodID === EPMethod.SingleDoc ? (
                                    <EPEditor />
                                ) : (
                                    <>
                                        <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 8}}>
                                            <label
                                                htmlFor="ep-font-select"
                                                style={{marginRight: 8, alignSelf: 'center'}}
                                            >
                                                Font:
                                            </label>
                                            <select
                                                id="ep-font-select"
                                                name="ep-font-select"
                                                aria-label="Select engraved plate font"
                                                value={this.font}
                                                onChange={this.onFontChange}
                                            >
                                                {FONT_OPTIONS.map(f => (
                                                    <option
                                                        key={f.value}
                                                        value={f.value}
                                                    >
                                                        {f.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {this.lines.map((ln, idx) => (
                                            <div
                                                key={idx}
                                                style={{display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 6}}
                                            >
                                                <div style={{display: 'flex', flexDirection: 'row', gap: 8}}>
                                                    <button
                                                        className="toggle-btn"
                                                        type="button"
                                                        aria-label={`Toggle size for line ${idx + 1}`}
                                                        onClick={() => this.toggleSize(idx)}
                                                        style={{width: 80}}
                                                    >
                                                        {ln.lineHeight === LineHeight.S ? 'Small' : ln.lineHeight === LineHeight.M ? 'Medium' : 'Large'}
                                                    </button>

                                                    <button
                                                        className="toggle-btn"
                                                        type="button"
                                                        aria-label={`Toggle bold for line ${idx + 1}`}
                                                        onClick={() => this.toggleBold(idx)}
                                                        style={{fontWeight: ln.bold ? '700' : '400'}}
                                                    >
                                                        B
                                                    </button>

                                                    <button
                                                        className="toggle-btn"
                                                        type="button"
                                                        aria-label={`Toggle italic for line ${idx + 1}`}
                                                        onClick={() => this.toggleItalic(idx)}
                                                        style={{
                                                            fontStyle: ln.italic ? 'italic' : 'normal',
                                                            fontWeight: ln.italic ? '700' : '400',
                                                        }}
                                                    >
                                                        I
                                                    </button>

                                                    <button
                                                        className="ep-btn"
                                                        type="button"
                                                        aria-label={`Remove line ${idx + 1}`}
                                                        onClick={() => this.removeLine(idx)}
                                                        disabled={this.lines.length === 1}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                                <input
                                                    id={`ep-${idx + 1}`}
                                                    type="text"
                                                    className="ep-text"
                                                    maxLength={30}
                                                    aria-label={`Line ${idx + 1} of the engraved plate`}
                                                    placeholder={`Line ${idx + 1}`}
                                                    value={ln.text}
                                                    onChange={e => this.onLineTextChange(idx, e.target.value)}
                                                    style={{
                                                        flex: 1,
                                                        fontWeight: ln.bold ? '700' : '400',
                                                        fontSize: ln.size,
                                                        fontStyle: ln.italic ? 'italic' : 'normal',
                                                        fontFamily: engravedPlateExtendedStore.fontFamily,
                                                    }}
                                                />
                                            </div>
                                        ))}

                                        <div style={{marginTop: 8, display: 'flex', justifyContent: 'flex-end'}}>
                                            <button
                                                className="ep-btn"
                                                type="button"
                                                onClick={this.addLine}
                                                disabled={this.lines.length === 10}
                                                aria-label="Add more engraved plate lines"
                                            >
                                                Add line
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="ep-option">
                                {s.prod.EngravedPlateStatusID === 5 && (
                                    <div className="ep-color">
                                        <select
                                            id="ep-color-select"
                                            name="ep-color-select"
                                            onChange={e => {
                                                s.ps.ep_id_view = parseInt(e.target.value, 10);
                                                if (s.ps.ep_id > 0) {
                                                    s.ps.ep_id = s.ps.ep_id_view;
                                                    s.ui.doRender = true;
                                                    s.ui.update_pricing = true;
                                                    s.pulse();
                                                }
                                            }}
                                            aria-label="Select engraved plate color"
                                        >
                                            {s.ps.ins_gold_allowed && <option value="37">Gold</option>}
                                            {s.ps.ins_silver_allowed && <option value="39">Silver</option>}
                                        </select>
                                    </div>
                                )}

                                {s.prod.EngravedPlateStatusID !== 1 && s.prod.EngravedPlateStatusID !== 2 && s.prod.EngravedPlateStatusID !== 6 && (
                                    <div className="ep-toggle">
                                        {(!s.ps.ep_id || s.ps.ep_id <= 0) && (
                                            <button
                                                type="button"
                                                aria-label="Add engraved plate to product"
                                                onClick={this.ep_add}
                                            >
                                                Add Engraved Plate
                                            </button>
                                        )}
                                        {s.ps.ep_id > 0 && (
                                            <button
                                                type="button"
                                                aria-label="Remove engraved plate from product"
                                                onClick={this.ep_remove}
                                            >
                                                Remove Engraved Plate
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="ep-link mb-1">
                                View information about engraved plates
                                <InfoIcon
                                    id="modal-sample-ep"
                                    ariaLabel="View more information about engraved plates"
                                    useMargin={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
